// ============================================================================
// Publish & Sync Dashboard — shared types, constants, and helpers.
//
// Extracted from PublishSyncDashboard.tsx to keep the main component focused
// on state management and composition. These are pure (no React) so they can
// be unit-tested independently and tree-shaken when unused.
// ============================================================================

import { CheckCircle2, Clock, TrendingUp, XCircle, Shield, Globe, Layers } from 'lucide-react';
import { formatRelative } from '@/lib/finance/financeFormat';
import type { SafeAdapterConfig } from '@/lib/finance/automation/publishAdapter';
import type { ExternalSyncAuthorityMode } from '@/lib/finance/workflowMode';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface PublishSyncEntry {
  id: string;
  /** Internal document number (e.g. 'BIL-2026-24821', 'DIN9860'). */
  item_number?: string | null;
  file_name: string | null;
  vendor_name: string | null;
  amount: number | null;
  currency: string;
  document_date: string | null;
  lifecycle_status: string;
  reviewed_at: string | null;
  created_at: string;
  external_reference: string | null;
  external_system: string | null;
  auto_published: boolean;
  auto_publish_adapter: string | null;
  auto_publish_error: string | null;
  workspace: string;
  /** Which OCR/vision provider produced the AI extraction ('total-vision' | 'gemini' | null). */
  extraction_source?: string | null;
  /** Model name used by the provider (e.g. 'glm-ocr', 'gemini-2.5-flash'). */
  extraction_model?: string | null;
  /** True when this entry is a promoted expense/supplier_invoice (not a DIN). */
  promoted?: boolean;
  /** The work item type ('expense' | 'supplier_invoice') for promoted entries. */
  item_type?: string;
  /** The work item subtype for promoted entries (e.g. 'fuel', 'purchase'). */
  item_subtype?: string | null;
  /** For DIN rows: the EXP/BIL produced when this DIN was materialised. */
  produced_doc_id?: string | null;
  produced_doc_number?: string | null;
  produced_doc_item_type?: string | null;
  produced_external_ref?: string | null;
  produced_external_system?: string | null;
}

export interface PublishSyncStats {
  ready: number;
  published: number;
  failed: number;
  toReview: number;
  queued: number;
  total: number;
}

export type FilterStatus = 'all' | 'ready' | 'published' | 'failed' | 'to_review' | 'queued' | 'conflicts' | 'archive';
export type SyncDirection = 'outbound' | 'inbound';

/**
 * Lifecycle statuses displayed under each outbound filter tab. The API
 * fetches (and the stat cards count) several statuses under one tab —
 * e.g. 'to_review' loads DINs in 'to_review' AND 'new', and 'published'
 * loads 'published' AND 'completed' — so each tab must render all of them
 * or the stat count and the table disagree.
 * 'conflicts' renders in its own panel, not the document table.
 */
export const FILTER_LIFECYCLE_STATUSES: Record<Exclude<FilterStatus, 'all'>, string[]> = {
  to_review: ['to_review', 'new'],
  ready: ['ready'],
  queued: ['approved'],
  published: ['published', 'completed'],
  failed: ['failed'],
  archive: ['published', 'completed'],
  conflicts: [],
};

/**
 * Whether an entry belongs in the given adapter's document view.
 *
 * Entries with no external attribution (both `external_system` and
 * `auto_publish_adapter` null) are shown under every connected adapter —
 * a document that has not been published yet, or whose publish attempt
 * failed, never gets an adapter recorded (those fields are only written on
 * a successful publish), so requiring attribution would hide failed /
 * to-review documents entirely.
 */
export function entryMatchesAdapter(entry: PublishSyncEntry, adapterId: string): boolean {
  return (
    entry.external_system === adapterId
    || entry.auto_publish_adapter === adapterId
    || (!entry.external_system && !entry.auto_publish_adapter)
  );
}

// ---------------------------------------------------------------------------
// Health configuration & tones
// ---------------------------------------------------------------------------

/** Sync health threshold configuration — configurable via env vars with sensible defaults. */
export const SYNC_HEALTH_CONFIG = {
  staleSyncThresholdDays: Number(process.env.NEXT_PUBLIC_STALE_SYNC_THRESHOLD_DAYS) || 7,
  staleReadyThresholdDays: Number(process.env.NEXT_PUBLIC_STALE_READY_THRESHOLD_DAYS) || 3,
};

/** Stale threshold — syncs older than this are flagged as needing attention. */
export const STALE_SYNC_THRESHOLD_DAYS = SYNC_HEALTH_CONFIG.staleSyncThresholdDays;
/** Stale threshold for outbound documents sitting in "ready" state. */
export const STALE_READY_THRESHOLD_DAYS = SYNC_HEALTH_CONFIG.staleReadyThresholdDays;

/** Health badge tones matching FinanceDashboard's KPI_TONE_CLASSES pattern. */
export const HEALTH_TONES = {
  healthy: {
    badge: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
    dot: 'bg-emerald-500',
    border: 'border-emerald-300 dark:border-emerald-700',
    bg: 'bg-emerald-50 dark:bg-emerald-900/20',
    text: 'text-emerald-700 dark:text-emerald-300',
  },
  warning: {
    badge: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
    dot: 'bg-amber-500',
    border: 'border-amber-300 dark:border-amber-700',
    bg: 'bg-amber-50 dark:bg-amber-900/20',
    text: 'text-amber-700 dark:text-amber-300',
  },
  critical: {
    badge: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
    dot: 'bg-red-500',
    border: 'border-red-300 dark:border-red-700',
    bg: 'bg-red-50 dark:bg-red-900/20',
    text: 'text-red-700 dark:text-red-300',
  },
  neutral: {
    badge: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300',
    dot: 'bg-slate-400',
    border: 'border-slate-300 dark:border-slate-700',
    bg: 'bg-slate-50 dark:bg-slate-800/40',
    text: 'text-slate-600 dark:text-slate-300',
  },
} as const;

export type HealthTone = keyof typeof HEALTH_TONES;

// ---------------------------------------------------------------------------
// Health calculation helpers
// ---------------------------------------------------------------------------

/** Determine adapter sync health from lastSyncedAt and lastError. */
export function getAdapterHealth(config: SafeAdapterConfig): { tone: HealthTone; label: string; detail: string } {
  if (!config.connected) {
    return { tone: 'neutral', label: 'Disconnected', detail: 'Not connected' };
  }
  if (config.lastError) {
    return { tone: 'critical', label: 'Error', detail: config.lastError };
  }
  if (!config.lastSyncedAt) {
    return { tone: 'warning', label: 'Never synced', detail: 'Connected but no sync run yet' };
  }
  const { ageDays } = formatRelative(config.lastSyncedAt);
  if (ageDays === null) {
    return { tone: 'warning', label: 'Never synced', detail: 'Connected but no sync run yet' };
  }
  if (ageDays > STALE_SYNC_THRESHOLD_DAYS) {
    return { tone: 'warning', label: 'Stale', detail: `Last sync ${Math.floor(ageDays)}d ago — sync recommended` };
  }
  return { tone: 'healthy', label: 'Healthy', detail: `Last sync ${formatRelative(config.lastSyncedAt).label}` };
}

/** Unified alert shape for health panels. */
export interface HealthAlert {
  severity: 'critical' | 'warning' | 'info';
  message: string;
  action?: string;
  adapterId?: string;
}

export interface HealthResult {
  tone: HealthTone;
  label: string;
  alerts: HealthAlert[];
}

/** Determine outbound pipeline health from stats and adapter connection state. */
export function getOutboundHealth(
  stats: PublishSyncStats,
  adapterConfigs: SafeAdapterConfig[],
  conflictCount = 0,
  adjustmentFailureCount = 0,
): HealthResult {
  const connectedCount = adapterConfigs.filter((c) => c.connected).length;
  const hasConnectedAdapter = connectedCount > 0;
  const alerts: HealthAlert[] = [];

  // Warn when there are documents that could be published but no adapter to publish to.
  if (!hasConnectedAdapter && (stats.ready > 0 || stats.toReview > 0 || stats.failed > 0)) {
    alerts.push({
      severity: 'warning',
      message: 'No accounting system connected — connect one to publish ready documents',
      action: 'Connect',
    });
  }

  if (stats.failed > 0) {
    alerts.push({ severity: 'critical', message: `${stats.failed} document${stats.failed !== 1 ? 's' : ''} failed to publish`, action: 'Review failures' });
  }
  if (adjustmentFailureCount > 0) {
    alerts.push({ severity: 'warning', message: `${adjustmentFailureCount} recent adjustment${adjustmentFailureCount !== 1 ? 's' : ''} failed — check sync job history` });
  }
  if (conflictCount > 0) {
    alerts.push({ severity: 'warning', message: `${conflictCount} sync conflict${conflictCount !== 1 ? 's' : ''} need review`, action: 'Review conflicts' });
  }
  if (stats.toReview > 0) {
    alerts.push({ severity: 'warning', message: `${stats.toReview} document${stats.toReview !== 1 ? 's' : ''} awaiting review`, action: 'Review now' });
  }
  if (stats.ready > 0) {
    alerts.push({ severity: 'info', message: `${stats.ready} document${stats.ready !== 1 ? 's' : ''} ready to publish`, action: hasConnectedAdapter ? 'Publish' : undefined });
  }
  const tone = alerts.some((a) => a.severity === 'critical') ? 'critical'
    : alerts.some((a) => a.severity === 'warning') ? 'warning'
    : 'healthy';
  const label = tone === 'critical' ? 'Action needed'
    : tone === 'warning' ? 'Needs attention'
    : stats.total > 0 ? 'All clear' : 'Idle';
  return { tone, label, alerts };
}

/** Determine inbound sync health across all connected adapters. */
export function getInboundHealth(
  configs: SafeAdapterConfig[],
  conflictCount = 0,
): HealthResult {
  const connected = configs.filter((c) => c.connected);
  const alerts: HealthAlert[] = [];

  if (connected.length === 0) {
    return { tone: 'neutral', label: 'No systems', alerts: [{ severity: 'info', message: 'No accounting systems connected — connect one to start syncing' }] };
  }

  for (const config of connected) {
    const health = getAdapterHealth(config);
    if (health.tone === 'critical') {
      alerts.push({ severity: 'critical', message: `${config.label}: ${health.detail}`, adapterId: config.id, action: 'Sync now' });
    } else if (health.tone === 'warning') {
      alerts.push({ severity: 'warning', message: `${config.label}: ${health.detail}`, adapterId: config.id, action: 'Sync now' });
    }
  }

  if (conflictCount > 0) {
    alerts.push({ severity: 'warning', message: `${conflictCount} unresolved sync conflict${conflictCount !== 1 ? 's' : ''}`, action: 'Review conflicts' });
  }

  const tone = alerts.some((a) => a.severity === 'critical') ? 'critical'
    : alerts.some((a) => a.severity === 'warning') ? 'warning'
    : 'healthy';
  const label = tone === 'critical' ? 'Action needed'
    : tone === 'warning' ? 'Needs attention'
    : 'All systems healthy';
  return { tone, label, alerts };
}

// ---------------------------------------------------------------------------
// Reconciliation health (Phase 5)
// ---------------------------------------------------------------------------

/** Variance alert summary for the reconciliation health badge. */
export interface ReconciliationAlertSummary {
  total_alerts: number;
  critical_count: number;
  warning_count: number;
  latest_alert_at: string | null;
}

/**
 * Determine reconciliation health from active variance alerts.
 * Used by the health overview panel to surface reconciliation issues.
 */
export function getReconciliationHealth(
  alerts: ReconciliationAlertSummary,
): HealthResult {
  const healthAlerts: HealthAlert[] = [];

  if (alerts.total_alerts === 0) {
    return {
      tone: 'healthy',
      label: 'Reconciled',
      alerts: [{ severity: 'info', message: 'No variance alerts — balances match', action: 'Run reconciliation' }],
    };
  }

  if (alerts.critical_count > 0) {
    healthAlerts.push({
      severity: 'critical',
      message: `${alerts.critical_count} critical variance alert${alerts.critical_count !== 1 ? 's' : ''} — balances do not match`,
      action: 'Review variances',
    });
  }

  if (alerts.warning_count > 0) {
    healthAlerts.push({
      severity: 'warning',
      message: `${alerts.warning_count} warning variance alert${alerts.warning_count !== 1 ? 's' : ''}`,
      action: 'Review variances',
    });
  }

  const tone = alerts.critical_count > 0 ? 'critical' : 'warning';
  const label = alerts.critical_count > 0 ? 'Variances found' : 'Needs review';
  return { tone, label, alerts: healthAlerts };
}

// ---------------------------------------------------------------------------
// Status & adapter label constants
// ---------------------------------------------------------------------------

export const STATUS_CONFIG: Record<string, { label: string; color: string; icon: typeof CheckCircle2 }> = {
  published: { label: 'Published', color: 'text-green-500', icon: CheckCircle2 },
  ready: { label: 'Ready to Publish', color: 'text-blue-500', icon: TrendingUp },
  failed: { label: 'Failed', color: 'text-red-500', icon: XCircle },
  to_review: { label: 'To Review', color: 'text-amber-500', icon: Clock },
  new: { label: 'New', color: 'text-gray-400', icon: Clock },
  processing: { label: 'Processing', color: 'text-purple-500', icon: Clock },
};

export const ADAPTER_LABELS: Record<string, string> = {
  xero: 'Xero',
  quickbooks: 'QuickBooks',
  sage: 'Sage',
  'sage-za': 'Sage (ZA)',
  dynamics365: 'Dynamics 365',
  gl: 'General Ledger',
  csv: 'CSV Export',
};

export function adapterLabel(id: string | null): string {
  if (!id) return '';
  return ADAPTER_LABELS[id] || id;
}

// ---------------------------------------------------------------------------
// Authority model helpers (Phase 2)
// ---------------------------------------------------------------------------

/** Human-readable label for each authority mode. */
export function getAuthorityLabel(mode: ExternalSyncAuthorityMode | null | undefined): string {
  switch (mode) {
    case 'client_master': return 'Total Finance is source of truth';
    case 'external_master': return 'External system is source of truth';
    case 'hybrid': return 'Hybrid — per data type';
    default: return 'Not configured';
  }
}

/** Short label for badges. */
export function getAuthorityShortLabel(mode: ExternalSyncAuthorityMode | null | undefined): string {
  switch (mode) {
    case 'client_master': return 'TF Master';
    case 'external_master': return 'External Master';
    case 'hybrid': return 'Hybrid';
    default: return 'Not set';
  }
}

/** Badge styling (tone + icon) for each authority mode. */
export function getAuthorityBadge(mode: ExternalSyncAuthorityMode | null | undefined): {
  tone: HealthTone;
  icon: typeof Shield;
  label: string;
} {
  switch (mode) {
    case 'client_master':
      return { tone: 'healthy', icon: Shield, label: 'TF Master' };
    case 'external_master':
      return { tone: 'neutral', icon: Globe, label: 'External Master' };
    case 'hybrid':
      return { tone: 'warning', icon: Layers, label: 'Hybrid' };
    default:
      return { tone: 'neutral', icon: Shield, label: 'Not set' };
  }
}
